import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  DestroyRef,
  ElementRef,
  OnDestroy,
  OnInit,
  computed,
  inject,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { email, FormField, form, minLength, required, submit } from '@angular/forms/signals';
import { firstValueFrom, take } from 'rxjs';
import { CurrentUserService } from '../../../../common/current-user/current-user.service';
import {
  apiErrorMessage,
  apiErrorStatus,
} from '../../../../common/http/api-error-message';
import { LoadingInlineComponent } from '../../../../atoms/loading-inline/loading-inline.component';
import {
  InviteAgencyUserDto,
  InviteUserDto,
  InviteUserResultDto,
  UserRolesEnum,
} from '../../../../common/general-types/users.dto';
import { AgencyService } from '../../../../services/agency/agency.service';
import { UserService } from '../../../../services/user/user.service';
import { ToastNotificationService } from '../../../../atoms/toast-notification/toast-notification.service';
import { ImageService } from '../../../../services/image/image.service';

/**
 * Only the fields the brand picker renders. `BusinessAccount` and `BusinessAccountDto` are not
 * assignable to each other (nullability of `coverPhoto`/`subscriptionId`), which is what forced
 * the old `as BusinessAccountDto[]` cast over an `any[]`.
 */
interface AssignableBrand {
  id: string;
  companyName: string;
  companyLogo?: string;
}

const ADD_USER_FALLBACK = 'We could not add this team member. Please try again.';
const BRANDS_LOAD_FAILED =
  'We could not load your brands. You can still add the user and assign brands later.';

@Component({
  selector: 'mb-brand-team-add-user',
  imports: [FormField, LoadingInlineComponent],
  templateUrl: './brand-team-add-user.component.html',
  styleUrl: './brand-team-add-user.component.css',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BrandTeamAddUserComponent implements OnInit, AfterViewInit, OnDestroy {
  /** Which team the invite is for. Drives the endpoint, the payload and the brand picker. */
  readonly mode = input<'brand' | 'agency'>('brand');
  /** Required when `mode` is 'brand'. Passed in rather than read from CurrentUserService. */
  readonly brandId = input<string | undefined>(undefined);
  /** Required when `mode` is 'agency'. */
  readonly agencyId = input<string | undefined>(undefined);

  readonly close = output<boolean>();
  readonly confirmEvent = output<void>();

  private readonly addUserDrawerDialog =
    viewChild<ElementRef<HTMLDialogElement>>('addUserDrawerDialog');
  private readonly openAddUserDrawerButton =
    viewChild<ElementRef<HTMLButtonElement>>('openAddUserDrawerButton');

  private dialogCloseHandler?: () => void;

  readonly newUserModel = signal({
    email: '',
    firstName: '',
    lastName: '',
    jobPosition: '',
  });

  readonly newUserForm = form(this.newUserModel, (schemaPath) => {
    required(schemaPath.email, { message: 'Email is required' });
    email(schemaPath.email, { message: 'Enter a valid email address' });
    required(schemaPath.firstName, { message: 'First name is required' });
    minLength(schemaPath.firstName, 2, {
      message: 'First name must be at least 2 characters long',
    });
    required(schemaPath.lastName, { message: 'Last name is required' });
    minLength(schemaPath.lastName, 2, { message: 'Last name must be at least 2 characters long' });
    // `jobPosition` is intentionally unvalidated — it is optional on the brand invite and is
    // not part of the agency invite at all.
  });

  readonly isAgency = computed(() => this.mode() === 'agency');

  /** Owned by `submit()`, so there is no flag to clear by hand if the request throws. */
  readonly runningAddUser = computed(() => this.newUserForm().submitting());

  /** Message for a failed submit. Cleared on dismiss and at the start of each attempt. */
  readonly submitError = signal<string | undefined>(undefined);

  /**
   * Set when the parent could not tell us which brand/agency this is for. Blocks the request
   * outright — the old code posted `businessAccountId: undefined` and got an opaque error back.
   */
  readonly contextError = computed(() => {
    if (this.isAgency()) {
      return this.agencyId()?.trim()
        ? undefined
        : 'We could not tell which agency to add this person to. Close this panel, reselect your agency and try again.';
    }
    return this.brandId()?.trim()
      ? undefined
      : 'We could not tell which brand to add this person to. Close this panel, reselect your brand and try again.';
  });

  /**
   * Save stays clickable on an invalid form on purpose: `submit()` guarantees no request is
   * made while invalid, and it marks every field touched so the per-field errors appear. A
   * button that starts greyed out gives the user nothing to act on and drops out of the tab
   * order. It is disabled only when clicking genuinely cannot help.
   */
  readonly canSubmit = computed(() => !this.runningAddUser() && !this.contextError());

  readonly displayBrands = signal<AssignableBrand[] | undefined>(undefined);
  readonly brandsError = signal<string | undefined>(undefined);
  readonly selectedBrands = signal<string[]>([]);

  /** First selected id so `el-select` has a stable `value`; multi-select state lives in `selectedBrands`. */
  readonly brandSelectControlValue = computed(() => this.selectedBrands()[0] ?? '');

  /** Services */
  private readonly currentUserService = inject(CurrentUserService);
  private readonly userService = inject(UserService);
  private readonly agencyService = inject(AgencyService);
  private readonly toastNotificationService = inject(ToastNotificationService);
  private readonly imageService = inject(ImageService);
  private readonly destroyRef = inject(DestroyRef);
  /** Services - END */

  ngOnInit(): void {
    if (!this.isAgency()) {
      return;
    }

    const cached = this.brandsFromState();
    if (cached) {
      this.displayBrands.set(cached);
      return;
    }

    const agencyId = this.agencyId()?.trim();
    if (!agencyId) {
      // Never leave the picker spinning forever, which is what the old code did whenever
      // CurrentUserService.myBrands happened to be undefined.
      this.displayBrands.set([]);
      this.brandsError.set(BRANDS_LOAD_FAILED);
      return;
    }

    this.agencyService
      .getAgencyById(agencyId)
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (agency) => this.displayBrands.set(this.toAssignableBrands(agency.businessAccounts)),
        error: (error: unknown) => {
          this.displayBrands.set([]);
          this.brandsError.set(apiErrorMessage(error, BRANDS_LOAD_FAILED));
        },
      });
  }

  /**
   * The agency's own brands, which is what an agency invite assigns. `myBrands` is only a
   * backstop: it comes from the login payload and holds the *user's* brands, so it is both
   * often undefined and the wrong list for this picker.
   */
  private brandsFromState(): AssignableBrand[] | undefined {
    const fromAgency = this.toAssignableBrands(
      this.currentUserService.focussedAgency?.agency?.businessAccounts,
    );
    if (fromAgency.length) {
      return fromAgency;
    }
    const fromUser = this.toAssignableBrands(this.currentUserService.myBrands);
    return fromUser.length ? fromUser : undefined;
  }

  /** Narrows loosely-typed state into the shape the picker needs, dropping deleted brands. */
  private toAssignableBrands(value: unknown): AssignableBrand[] {
    if (!Array.isArray(value)) {
      return [];
    }
    return value
      .filter((item): item is Record<string, unknown> => !!item && typeof item === 'object')
      .filter(
        (item) => typeof item['id'] === 'string' && typeof item['companyName'] === 'string',
      )
      .filter((item) => item['isDeleted'] !== true)
      .map((item) => ({
        id: item['id'] as string,
        companyName: item['companyName'] as string,
        companyLogo: typeof item['companyLogo'] === 'string' ? item['companyLogo'] : undefined,
      }));
  }

  brandLogoSrc(brand: AssignableBrand): string {
    return brand.companyLogo
      ? this.imageService.getImageSource(brand.companyLogo, 'gallery')
      : 'https://ionicframework.com/docs/img/demos/avatar.svg';
  }

  isBrandSelected(brandId: string): boolean {
    return this.selectedBrands().includes(brandId);
  }

  assignBrandsTriggerText(): string {
    const ids = this.selectedBrands();
    const brands = this.displayBrands();
    if (ids.length === 0) {
      return 'Select brands';
    }
    if (ids.length === 1 && brands) {
      const match = brands.find((b) => b.id === ids[0]);
      return match?.companyName ?? '1 brand';
    }
    return `${ids.length} brands selected`;
  }

  assignBrandsTriggerLogo(): string {
    const ids = this.selectedBrands();
    const brands = this.displayBrands();
    if (ids.length === 0 || !brands?.length) {
      return 'https://ionicframework.com/docs/img/demos/avatar.svg';
    }
    const first = brands.find((b) => b.id === ids[0]);
    return first ? this.brandLogoSrc(first) : 'https://ionicframework.com/docs/img/demos/avatar.svg';
  }

  onBrandToggle(brandId: string, event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    this.selectedBrands.update((ids) =>
      ids.includes(brandId) ? ids.filter((id) => id !== brandId) : [...ids, brandId]
    );
    // `@tailwindplus/elements` `el-select` closes the list on every `el-option` click
    // (`setSelectedOption` → `hidePopover`). Re-open immediately so multi-select can continue
    // without reopening the dropdown.
    const optionEl = event.currentTarget;
    if (optionEl instanceof HTMLElement) {
      const panel = optionEl.closest('el-options');
      if (panel instanceof HTMLElement) {
        queueMicrotask(() => {
          panel.showPopover();
        });
      }
    }
  }

  closeDrawer(): void {
    this.close.emit(true);
  }

  dismissSubmitError(): void {
    this.submitError.set(undefined);
  }

  /**
   * Bound to Save. `submit()` marks every field touched, refuses to run `action` while the form
   * is invalid, and owns the in-flight flag.
   *
   * Returns the in-flight promise so a caller can await the whole attempt; the template
   * ignores it.
   */
  async saveTeamMember(): Promise<void> {
    const context = this.contextError();
    if (context) {
      this.submitError.set(context);
      return;
    }

    await submit(this.newUserForm, {
      action: async () => {
        this.submitError.set(undefined);
        try {
          const result = this.isAgency()
            ? await this.inviteAgencyMember()
            : await this.inviteBrandMember();
          this.announceSuccess(result);
          this.confirmEvent.emit();
          return undefined;
        } catch (error: unknown) {
          const message = apiErrorMessage(error, ADD_USER_FALLBACK);
          this.submitError.set(message);
          // A conflict is always about the email address, so show it against that field too.
          if (apiErrorStatus(error) === 409) {
            return [{ fieldTree: this.newUserForm.email, kind: 'server', message }];
          }
          return undefined;
        }
      },
      onInvalid: () => {
        this.submitError.set('Please fix the highlighted fields and try again.');
      },
    });
  }

  private async inviteBrandMember(): Promise<InviteUserResultDto> {
    const businessAccountId = this.brandId()?.trim();
    if (!businessAccountId) {
      throw new Error(this.contextError() ?? ADD_USER_FALLBACK);
    }
    const model = this.newUserModel();
    const payload: InviteUserDto = {
      businessAccountId,
      email: model.email.trim(),
      firstName: model.firstName.trim(),
      lastName: model.lastName.trim(),
      jobPosition: model.jobPosition.trim(),
      role: UserRolesEnum.Basic,
      photoURL: '',
      isActive: false,
    };
    return await firstValueFrom(this.userService.inviteBusinessUser(payload).pipe(take(1)));
  }

  private async inviteAgencyMember(): Promise<InviteUserResultDto> {
    const agencyId = this.agencyId()?.trim();
    if (!agencyId) {
      throw new Error(this.contextError() ?? ADD_USER_FALLBACK);
    }
    const model = this.newUserModel();
    const payload: InviteAgencyUserDto = {
      agencyId,
      email: model.email.trim(),
      firstName: model.firstName.trim(),
      lastName: model.lastName.trim(),
      role: UserRolesEnum.Basic,
      photoURL: '',
      isActive: false,
      businessAccounts: this.selectedBrands(),
    };
    return await firstValueFrom(this.userService.inviteAgencyUser(payload).pipe(take(1)));
  }

  private announceSuccess(result: InviteUserResultDto): void {
    const model = this.newUserModel();
    const name =
      [result.user?.firstName ?? model.firstName, result.user?.lastName ?? model.lastName]
        .filter((part) => part?.trim())
        .join(' ')
        .trim() || model.email.trim();

    if (result.emailQueued === false) {
      void this.toastNotificationService.alert({
        title: 'Team member added',
        message: `${name} was added, but we could not send the invite email. Ask them to sign in and reset their password.`,
        type: 'warning',
        icon: 'warning',
      });
      return;
    }

    void this.toastNotificationService.alert({
      title: 'Team member added',
      message:
        result.userState === 'existing'
          ? `${name} already had a SHOUT account and now has access.`
          : `Invite sent to ${name}.`,
      type: 'success',
      icon: 'success',
    });
  }

  ngAfterViewInit(): void {
    const opener = this.openAddUserDrawerButton()?.nativeElement;
    if (opener) {
      setTimeout(() => opener.click(), 0);
    }

    const dialog = this.addUserDrawerDialog()?.nativeElement;
    if (dialog) {
      this.dialogCloseHandler = () => this.closeDrawer();
      dialog.addEventListener('close', this.dialogCloseHandler);
    }
  }

  ngOnDestroy(): void {
    // Detach the listener. The old implementation *called* it here, which re-emitted `close`
    // on teardown and left the listener attached.
    const dialog = this.addUserDrawerDialog()?.nativeElement;
    if (dialog && this.dialogCloseHandler) {
      dialog.removeEventListener('close', this.dialogCloseHandler);
    }
    this.dialogCloseHandler = undefined;
  }
}
